import { FC, ReactElement, useState } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

interface HyperlinkFormProps {
    disabled: boolean;
    onAddHyperlink: (name: string, url: string) => Promise<void>;
}

export const HyperlinkForm: FC<HyperlinkFormProps> = ({
    disabled,
    onAddHyperlink,
}): ReactElement => {
    const { t } = useTranslation();
    const [linkUrl, setLinkUrl] = useState('');
    const [linkName, setLinkName] = useState('');
    const [isAdding, setIsAdding] = useState(false);

    const handleAddHyperlink = async () => {
        if (!linkUrl.trim()) return;
        
        setIsAdding(true);
        try {
            await onAddHyperlink(linkName.trim() || linkUrl.trim(), linkUrl.trim());
            setLinkUrl('');
            setLinkName('');
        } finally {
            setIsAdding(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && linkUrl.trim()) {
            handleAddHyperlink();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#374151', mb: 0.5, fontWeight: 500 }}>
                    {t('attachment_modal.hyperlink_name', 'Hyperlink Name (optional)')}
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={t('attachment_modal.enter_link_name', 'Enter link name...')}
                    value={linkName}
                    onChange={(e) => setLinkName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isAdding}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            fontSize: '0.8125rem',
                            borderRadius: '6px',
                            '& fieldset': { borderColor: '#D0D5DD' },
                            '&:hover fieldset': { borderColor: '#98A2B3' },
                        },
                    }}
                />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#374151', mb: 0.5, fontWeight: 500 }}>
                    {t('attachment_modal.url', 'URL')}
                </Typography>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isAdding}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            fontSize: '0.8125rem',
                            borderRadius: '6px',
                            '& fieldset': { borderColor: '#D0D5DD' },
                            '&:hover fieldset': { borderColor: '#98A2B3' },
                        },
                    }}
                />
            </Box>
            <Button
                variant="contained"
                onClick={handleAddHyperlink}
                disabled={!linkUrl.trim() || disabled || isAdding}
                startIcon={<AddIcon />}
                sx={{
                    backgroundColor: '#B8860B',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    borderRadius: '6px',
                    boxShadow: 'none',
                    alignSelf: 'flex-start',
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                        backgroundColor: '#996B09',
                        boxShadow: 'none',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#E5E7EB',
                        color: '#98A2B3',
                    },
                }}
            >
                {t('attachment_modal.add_link', 'Add Link')}
            </Button>
        </Box>
    );
};
